/**
 * Darf der angemeldete Anwender Mitglieder **einladen** bzw. Konten
 * **aktivieren/deaktivieren**?
 *
 * 🔴 Serverseitig haengen `members.invite`, `members.activate` und
 * `members.deactivate` an der **Organisations-Rolle** (`requireRoleAdminRaw` →
 * owner/admin), nicht an einem Entitlement. Ein `usePermissions()`-Gate waere
 * deshalb ein Gate auf ein *anderes* Recht als das des Servers: entweder zu
 * streng (Flaeche unerreichbar) oder zu lax (403 nach dem Ausfuellen).
 *
 * ⚪ Die Schwester-Apps loesen den Wert in ihrem `OrganizationGuard` auf. Diese
 * App hat keinen — sie verlangt keine eingerichtete Organisation —, deshalb
 * steht die Ableitung hier als eigener Hook. Quelle und Rollenmenge sind
 * dieselben wie dort (`saas-dashandwerk-frontend`, `saas-verein-frontend`).
 */

import { trpc } from '@/trpc'

/**
 * Rollen, die als Organisations-Administration gelten. Better-Auth schreibt
 * 'owner' klein, aeltere Datensaetze fuehren 'Owner'/'Administrator' gross.
 */
const ADMIN_ROLES = new Set(['owner', 'Owner', 'admin', 'Admin', 'administrator', 'Administrator'])

/**
 * Hat der angemeldete Nutzer die Organisations-Rolle owner/admin?
 *
 * Waehrend die Abfrage laeuft: `false`. „Weiss nicht" muss hier „nein" heissen —
 * jeder Knopf dahinter endet in einer Mutation.
 */
function useIsOrgAdminRole(): boolean {
  const { data } = trpc.organization.current.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
  })
  const rolle = data?.userRole
  return rolle !== undefined && ADMIN_ROLES.has(rolle)
}

/**
 * Darf der angemeldete Nutzer Kollegen **einladen**? (`members.invite`)
 *
 * Ohne diesen Wert faellt `canInvite` auf seinen Default `false` — der
 * Einladen-Knopf verschwaende dann fuer ALLE, auch fuer den Owner.
 */
export function useCanInviteMembers(): boolean {
  return useIsOrgAdminRole()
}

/**
 * Darf der angemeldete Nutzer Konten **aktivieren/deaktivieren**?
 * (`members.activate` / `members.deactivate`)
 *
 * Dieselbe Grenze wie beim Einladen, nur an anderen Prozeduren — die
 * Mitglieder-Seiten sind bereits ab `members:member:read` erreichbar.
 */
export function useCanManageMemberStatus(): boolean {
  return useIsOrgAdminRole()
}

/**
 * Darf der angemeldete Nutzer **Rollen anlegen und loeschen**? (`role.create` / `role.delete`)
 *
 * Dieselbe Grenze wie beim Einladen (`requireRoleAdmin`, owner/admin). Ohne
 * diesen Wert faellt `canManageRoles` (core-frontend >= 5.157.0) auf seinen
 * Default `false` — „Neue Rolle“, Loeschen und die Aktionsspalte
 * verschwaenden dann fuer ALLE, auch fuer den Owner (ENTSCHEID observer 13211).
 */
export function useCanManageRoles(): boolean {
  return useIsOrgAdminRole()
}

/**
 * Ist der angemeldete Nutzer **Eigentuemer** der Organisation?
 * (`members.requestOwnershipTransfer`)
 *
 * 🔴 **Nicht** `useIsOrgAdminRole()`. Der Eigentuemerwechsel ist die eine
 * Prozedur, die ein Admin ausdruecklich NICHT darf: der Server prueft
 * `initiatorMember[0]?.role !== 'owner'` und antwortet sonst mit FORBIDDEN
 * („Only the organization owner can initiate an ownership transfer").
 * Wer hier `admin` durchliesse, baute genau die Sackgasse, die
 * `canInvite`/`canManageStatus` abschaffen.
 *
 * 🔴 Und **exakt** `'owner'`, klein — dieselbe Zeichenkette, die der Server
 * vergleicht. Die Grosschreib-Varianten in `ADMIN_ROLES` sind hier bewusst
 * NICHT erlaubt: ein Datensatz mit `'Owner'` bekaeme vom Server ein 403, ein
 * Knopf dafuer waere eine Einladung in den Fehler.
 */
export function useIsOrgOwner(): boolean {
  const { data } = trpc.organization.current.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
  })
  return data?.userRole === 'owner'
}

/**
 * Darf der angemeldete Nutzer die **Firmendaten** schreiben? (`sellerProfile.update`, Plan API 2026-11-01/01 E14)
 *
 * Lesen darf jedes Mitglied, schreiben Owner/Admin (P1-16) — dieselbe Rollengrenze wie beim Einladen. Wer sie nicht
 * hat, sieht die Seite gesperrt, mit Grund.
 */
export function useCanManageSellerProfile(): boolean {
  return useIsOrgAdminRole()
}
